import React, { useEffect, useState, useCallback } from "react";
import { API, graphqlOperation, Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { listInterviewSlots, listCandidateProfiles } from "../graphql/queries";
import Header from "../components/header/Header";
import { RiseLoader } from "react-spinners";
import { 
  FiPlay, 
  FiCalendar, 
  FiClock, 
  FiCheckCircle, 
  FiBarChart2, 
  FiList, 
  FiActivity,
  FiArrowRight,
  FiTarget,
  FiBook,
  FiTrendingUp,
  FiZap,
  FiMap,
  FiAward,
  FiRefreshCw
} from "react-icons/fi";
import ScreeningRoundModal from "../components/ScreeningRoundModal";
// Study Planner - temporarily hidden
// import GoalWizard from "../components/GoalWizard";
// import { useStudyPlanner } from "../hooks/useStudyPlanner";
// import { GOAL_TYPE_INFO } from "../services/StudyPlannerService";

function CandidateSchedule() {
  const navigate = useNavigate();
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const override = {
    display: "block",
    margin: "0 auto",
    borderColor: "#3b82f6",
  };

  // Get user info from localStorage
  const candidateProfile = localStorage.getItem("candidateProfile");
  const profileData = candidateProfile ? JSON.parse(candidateProfile) : {};
  const [userName, setUserName] = useState(profileData.fullName || '');
  const [isScreeningModalOpen, setIsScreeningModalOpen] = useState(false);
  const [fullCandidateProfile, setFullCandidateProfile] = useState(profileData);
  const [candidateEmail, setCandidateEmail] = useState('');
  const [candidateProfileId, setCandidateProfileId] = useState(profileData.id || null);
  
  // Study Planner - temporarily hidden
  // const [isGoalWizardOpen, setIsGoalWizardOpen] = useState(false);
  // const {
  //   goalType,
  //   goalData,
  //   hasGoal,
  //   hasRoadmap,
  //   overallProgress,
  //   roadmap,
  //   isLoading: isStudyPlannerLoading,
  //   startSession,
  //   connect,
  //   isConnected,
  //   error: studyPlannerError
  // } = useStudyPlanner(candidateProfileId);

  // // Check if we should show goal wizard after returning from screening interview
  // useEffect(() => {
  //   const showGoalWizard = localStorage.getItem("showGoalWizardAfterInterview");
  //   if (showGoalWizard === "true" && !hasGoal && !isStudyPlannerLoading) {
  //     // Clear the flag
  //     localStorage.removeItem("showGoalWizardAfterInterview");
  //     // Show the goal wizard
  //     setIsGoalWizardOpen(true);
  //   }
  // }, [hasGoal, isStudyPlannerLoading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user name from Auth if not in local storage
        if (!userName || userName === 'Candidate') {
          try {
            const user = await Auth.currentAuthenticatedUser();
            const name = user.attributes.name || user.attributes.given_name || user.attributes.email?.split('@')[0];
            if (name) setUserName(name);
          } catch (e) {
            console.log("Auth user fetch failed", e);
          }
        }

        const email = localStorage.getItem("candidateProfileEmailID");
        setCandidateEmail(email);

        if (email) {
          // Fetch full candidate profile for screening modal
          try {
            const profileResponse = await API.graphql(
              graphqlOperation(listCandidateProfiles, {
                filter: { email: { eq: email } },
                limit: 1,
              })
            );
            const profiles = profileResponse.data.listCandidateProfiles.items;
            if (profiles.length > 0) {
              setFullCandidateProfile(profiles[0]);
              setCandidateProfileId(profiles[0].id);
              // Update username from profile's fullName if available
              if (profiles[0].fullName) {
                setUserName(profiles[0].fullName);
              }
            }
          } catch (profileError) {
            console.log("Error fetching candidate profile:", profileError);
          }

          let nextToken = null;
          let interviewSlots = [];
          
          do {
            const interviewSlotsResponse = await API.graphql(
              graphqlOperation(listInterviewSlots, {
                filter: { candidateEmail: { eq: email } },
                limit: 100,
                nextToken: nextToken,
              })
            );

            interviewSlots.push(
              ...interviewSlotsResponse.data.listInterviewSlots.items
            );
            nextToken = interviewSlotsResponse.data.listInterviewSlots.nextToken;
          } while (nextToken);

          // Sort by date descending (newest first)
          interviewSlots.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setScheduledInterviews(interviewSlots);
        }
      } catch (error) {
        console.error("Error fetching interview slots:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle goal wizard completion
  // Study Planner - temporarily hidden
  // const handleGoalComplete = useCallback(async (goalData) => {
  //   try {
  //     // Connect to WebSocket if not connected
  //     if (!isConnected) {
  //       await connect();
  //     }
  //     
  //     // Start session with the goal
  //     await startSession(fullCandidateProfile, goalData);
  //     
  //     // Close wizard
  //     setIsGoalWizardOpen(false);
  //     
  //     // Navigate to study dashboard
  //     navigate('/study-dashboard');
  //   } catch (error) {
  //     console.error('Error starting study session:', error);
  //     // Still close wizard, user can retry from dashboard
  //     setIsGoalWizardOpen(false);
  //   }
  // }, [isConnected, connect, startSession, fullCandidateProfile, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        <Header />
        <div className="flex-1 flex justify-center items-center">
          <RiseLoader color={"#3b82f6"} loading={loading} css={override} size={15} />
        </div>
      </div>
    );
  }

  const EXPIRY_MS = 15 * 60 * 1000;
  const isInterviewExpired = (interview) => {
    if (interview.status === 'COMPLETED' || interview.candidateAttendance) return false;

    let linkSentAt = null;
    try {
      const cfg = interview.meetingConfig ? JSON.parse(interview.meetingConfig) : {};
      if (cfg.linkSentAt) {
        const t = new Date(cfg.linkSentAt).getTime();
        if (!isNaN(t)) linkSentAt = t;
      }
    } catch (e) {}

    if (linkSentAt !== null) {
      return Date.now() - linkSentAt > EXPIRY_MS;
    }

    const refs = [];
    if (interview.date && interview.startTime) {
      const scheduled = new Date(`${interview.date}T${interview.startTime}`);
      if (!isNaN(scheduled.getTime())) refs.push(scheduled.getTime());
    }
    if (interview.createdAt) {
      const created = new Date(interview.createdAt);
      if (!isNaN(created.getTime())) refs.push(created.getTime());
    }
    if (interview.updatedAt) {
      const updated = new Date(interview.updatedAt);
      if (!isNaN(updated.getTime())) refs.push(updated.getTime());
    }
    if (refs.length === 0) return false;
    const referenceTime = Math.max(...refs);
    return Date.now() - referenceTime > EXPIRY_MS;
  };

  const completedCount = scheduledInterviews.filter(s => s.status === 'COMPLETED').length;
  const activeCount = scheduledInterviews.filter(s => (s.status === 'SCHEDULED' || s.status === 'ACTIVE') && !isInterviewExpired(s)).length;

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 font-sans selection:bg-blue-500/30">
      <Header />
      
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Welcome Section - Centered at Top */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-medium text-white tracking-tight">
            Hello, {userName || 'Candidate'}
          </h1>
          <h2 className="text-slate-400 text-base mt-2">
            Are you ready to pursue your goal further?
          </h2>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Left Column: Action Buttons Card */}
          <div className="bg-slate-800/40 rounded-xl p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400">
                <FiActivity size={20} />
              </div>
              <h2 className="text-lg font-medium text-white">Get Started</h2>
            </div>
            <p className="text-slate-400 text-sm mb-5 leading-relaxed">
              Take the screening round to showcase your skills.
            </p>
            <div className="flex flex-col gap-3">
              {/* Study Room button - temporarily hidden
              <button
                onClick={() => {
                  if (hasGoal) {
                    navigate('/study-dashboard');
                  } else {
                    setIsGoalWizardOpen(true);
                  }
                }}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all flex items-center justify-center"
              >
                Enter Study Room
              </button>
              */}
              <button
                onClick={() => setIsScreeningModalOpen(true)}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all flex items-center justify-center"
              >
                Give Screening Round
              </button>
            </div>

            {/* Interview Stats */}
            <div className="flex-1 flex items-center justify-center border-t border-slate-700/50 mt-5">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-slate-400 text-sm">{completedCount} Completed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-slate-400 text-sm">{activeCount} Active</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: History */}
          <div className="bg-slate-800/20 rounded-xl overflow-hidden flex flex-col max-h-[400px]">
            <div className="px-6 py-4 flex justify-between items-center bg-slate-800/40 flex-shrink-0">
              <h3 className="text-white font-medium text-sm flex items-center gap-2">
                <FiList className="text-slate-400" /> Recent Activity
              </h3>
              <span className="text-xs text-slate-500">{scheduledInterviews.length} sessions</span>
            </div>

            <div className="divide-y divide-slate-800 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800/20">
              {scheduledInterviews.length === 0 ? (
                <div className="p-12 text-center text-slate-500 text-sm flex flex-col items-center">
                  <FiCalendar className="text-3xl mb-3 opacity-20" />
                  <p>No interviews scheduled yet.</p>
                </div>
              ) : (
                scheduledInterviews.map((interview, index) => {
                  const status = interview.status || 'SCHEDULED';
                  let meetingConfig = {};
                  try {
                    meetingConfig = interview.meetingConfig ? JSON.parse(interview.meetingConfig) : {};
                  } catch (e) {}

                  const isCompleted = status === 'COMPLETED';
                  const expired = isInterviewExpired(interview);
                  const isActive = (status === 'ACTIVE' || status === 'SCHEDULED') && !expired;

                  return (
                    <div 
                      key={interview.id || index}
                      className="px-6 py-4 hover:bg-slate-800/40 transition-colors flex items-center justify-between group"
                    >
                      <div className="flex-1 min-w-0 pr-6">
                        <h4 className="text-slate-200 font-medium text-sm truncate group-hover:text-blue-400 transition-colors">
                          {meetingConfig.jobTitle || 'General Interview'}
                        </h4>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-slate-500 text-xs flex items-center gap-1">
                             <FiClock size={10} />
                             {interview.createdAt ? new Date(interview.createdAt).toLocaleDateString('en-US', {
                               month: 'short',
                               day: 'numeric'
                             }) : 'Date not set'}
                          </span>
                          {isCompleted ? (
                            <FiCheckCircle className="text-emerald-500" size={14} />
                          ) : (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              expired ? 'text-slate-500 bg-slate-700/30' :
                              isActive ? 'text-blue-400 bg-blue-500/10' :
                              'text-slate-400 bg-slate-700/30'
                            }`}>
                              {expired ? 'INACTIVE' : status}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        {isActive && (
                          <button
                            onClick={() => navigate(`/public-interview/${interview.id}`)}
                            className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                          >
                            Join <FiArrowRight />
                          </button>
                        )}
                        
                        {isCompleted && (
                          <button
                            onClick={() => {
                              const candidateEmail = localStorage.getItem('candidateProfileEmailID');
                              navigate(`/CompanyCandidateViewProfilev1/${encodeURIComponent(candidateEmail)}?slotId=${interview.id}`);
                            }}
                            className="text-xs text-slate-400 hover:text-white transition-colors flex items-center gap-1 px-2 py-1"
                          >
                            Report <FiBarChart2 />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Screening Round Modal */}
      <ScreeningRoundModal
        isOpen={isScreeningModalOpen}
        onClose={() => setIsScreeningModalOpen(false)}
        candidateProfile={fullCandidateProfile}
        candidateEmail={candidateEmail}
        onScreeningCreated={(screeningSlot) => {
          // Navigate to the screening interview after a brief delay
          setTimeout(() => {
            navigate(`/public-interview/${screeningSlot.id}`);
          }, 1500);
        }}
      />

      {/* Goal Wizard Modal - temporarily hidden
      <GoalWizard
        isOpen={isGoalWizardOpen}
        onClose={() => setIsGoalWizardOpen(false)}
        onComplete={handleGoalComplete}
        candidateProfile={fullCandidateProfile}
        initialGoal={goalData}
      />
      */}
    </div>
  );
}

export default CandidateSchedule;
